import { optimizedImage } from '../../media/optimized-image';
import { AfterViewInit, Component, ElementRef, OnDestroy, computed, inject, input, signal } from '@angular/core';
import { SiteConfigService } from '../core/site-config.service';

type MediaSizes = Record<string, unknown> | unknown[] | null | undefined;
interface ImageCandidate {
  token: string;
  width: number;
}

@Component({
  selector: 'app-responsive-image',
  standalone: true,
  template: `
    @if (visible()) {
      <img
        [src]="src()"
        [srcset]="srcset()"
        [sizes]="displaySizes()"
        [alt]="alt()"
        [class.contain]="fit() === 'contain'"
        [attr.style]="intrinsicStyle()"
        [attr.width]="optimized()?.width || 720"
        [attr.height]="optimized()?.height || 540"
        decoding="async"
        [attr.fetchpriority]="priority() ? 'high' : 'auto'"
        [loading]="priority() ? 'eager' : 'lazy'"
      />
    }
  `,
  styles: [`
    :host {
      background:
        linear-gradient(135deg, rgb(38 28 24 / 12%), transparent),
        radial-gradient(circle, var(--accent), transparent 68%);
      box-sizing: border-box;
      display: block;
      height: auto;
      aspect-ratio: 1 / 1;
      min-height: 0;
      min-width: 0;
      overflow: hidden;
      width: 100%;
    }

    img {
      box-sizing: border-box;
      display: block;
      height: 100%;
      min-height: 0;
      min-width: 0;
      object-fit: cover;
      width: 100%;
    }

    img.contain { object-fit: contain; }
  `]
})
export class ResponsiveImageComponent implements AfterViewInit, OnDestroy {
  private readonly element = inject(ElementRef<HTMLElement>);
  private readonly site = inject(SiteConfigService);
  private observer?: IntersectionObserver;

  readonly url = input<string | null | undefined>(null);
  readonly mediaSizes = input<MediaSizes>(null);
  readonly alt = input('');
  readonly displaySizes = input('(max-width: 760px) 88vw, (max-width: 1100px) 44vw, 360px');
  readonly fit = input<'cover' | 'contain'>('cover');
  readonly priority = input(false);
  readonly focusX = input<number | string | null | undefined>(50);
  readonly focusY = input<number | string | null | undefined>(50);

  private readonly imageVisible = signal(false);
  protected readonly visible = computed(() => this.priority() || this.imageVisible());
  protected readonly optimized = computed(() => optimizedImage(this.url()));
  protected readonly src = computed(() => this.optimized()?.src ?? this.pickDefaultUrl());
  protected readonly srcset = computed(() => this.optimized()?.srcset ?? this.buildSrcset());
  protected readonly objectPosition = computed(() => `${this.focus(this.focusX())}% ${this.focus(this.focusY())}%`);

  // Some published themes set aspect-ratio:auto!important, overriding the
  // width/height presentational hint before image decode. Reserve priority media explicitly.
  protected readonly intrinsicStyle = computed(() => {
    const asset = this.optimized();
    return `object-position:${this.objectPosition()};` +
      (this.priority() && asset ? `aspect-ratio:${asset.width} / ${asset.height} !important;` : '');
  });

  ngAfterViewInit(): void {
    if (this.priority()) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      this.imageVisible.set(true);
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.imageVisible.set(true);
        this.observer?.disconnect();
      }
    }, {
      rootMargin: '260px 0px'
    });

    this.observer.observe(this.element.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private focus(value: unknown): number {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.min(100, number)) : 50;
  }

  private pickDefaultUrl(): string {
    const url = this.normalizeUrl(this.url() ?? '');
    if (!this.hasSizeToken(url)) {
      return url;
    }

    const candidates = this.imageCandidates();
    const fallback = candidates.find((candidate) => candidate.width >= 700) ?? candidates.at(-1) ?? { token: 'm' };
    return this.replaceSizeToken(url, fallback.token);
  }

  private buildSrcset(): string {
    const url = this.normalizeUrl(this.url() ?? '');
    if (!this.hasSizeToken(url)) {
      return '';
    }

    return this.imageCandidates()
      .map((candidate) => `${this.replaceSizeToken(url, candidate.token)} ${candidate.width}w`)
      .join(', ');
  }

  private hasSizeToken(url: string): boolean {
    return /\[size\]|\{size\}/i.test(url);
  }

  private replaceSizeToken(url: string, token: string): string {
    return url.replace(/\[size\]|\{size\}/gi, token);
  }

  private imageCandidates(): ImageCandidate[] {
    const candidates = new Map<string, ImageCandidate>();
    const sizes = this.mediaSizes();

    if (Array.isArray(sizes)) {
      for (const item of sizes) {
        this.collectCandidate(candidates, item);
      }
    } else if (sizes && typeof sizes === 'object') {
      for (const [key, value] of Object.entries(sizes)) {
        this.collectCandidate(candidates, key);
        this.collectCandidate(candidates, value);
      }
    }

    if (!candidates.size) {
      [
        { token: 's', width: 360 },
        { token: 'm', width: 720 },
        { token: 'b', width: 1200 }
      ].forEach((candidate) => candidates.set(candidate.token, candidate));
    }

    return [...candidates.values()].filter((candidate) => candidate.width > 0).sort((a, b) => a.width - b.width);
  }

  private collectCandidate(candidates: Map<string, ImageCandidate>, value: unknown): void {
    if (typeof value === 'number') {
      candidates.set(String(value), { token: String(value), width: value });
      return;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      const namedWidth = this.namedWidth(normalized);
      if (namedWidth > 0) {
        candidates.set(normalized, { token: normalized, width: namedWidth });
        return;
      }
      const width = Number(normalized.match(/\d+/)?.[0] ?? 0);
      if (width > 0) {
        candidates.set(normalized, { token: normalized, width });
      }
      return;
    }
    if (value && typeof value === 'object') {
      for (const nested of Object.values(value as Record<string, unknown>)) {
        this.collectCandidate(candidates, nested);
      }
    }
  }

  private namedWidth(value: string): number {
    const map: Record<string, number> = {
      xs: 180,
      s: 360,
      sm: 480,
      m: 720,
      md: 720,
      l: 960,
      lg: 960,
      b: 1200,
      xl: 1440,
      original: 1600
    };

    return map[value] ?? 0;
  }

  private normalizeUrl(url: string): string {
    const prepared = url.trim();
    if (!prepared || /^(?:data|blob):/i.test(prepared)) {
      return prepared;
    }

    const mediaBaseUrl = String(this.site.config().mediaBaseUrl ?? '').replace(/\/+$/, '');
    if (/^https?:\/\//.test(prepared)) {
      if (!mediaBaseUrl) return prepared;
      try {
        const parsed = new URL(prepared);
        return parsed.pathname.startsWith('/media/')
          ? `${mediaBaseUrl}${parsed.pathname}${parsed.search}${parsed.hash}`
          : prepared;
      } catch {
        return prepared;
      }
    }

    const path = `/${prepared.replace(/^\/+/, '')}`;
    return mediaBaseUrl && path.startsWith('/media/') ? `${mediaBaseUrl}${path}` : path;
  }
}
